/**
 * Storage Utility
 * Secure storage management for tokens, user data, and application state
 */

import { API_CONFIG } from '../config/api.config';

// Types
export interface StoredUser {
  id: number | string;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  role?: any;
  roleName?: string;
  roleCode?: string;
  department?: string;
  phone?: string;
  avatar?: string;
  isActive: boolean;
  blocked?: boolean;
  permissions: string[];
}

class StorageService {
  private storage: Storage;

  // Access tokens are kept in memory only (never persisted) so they cannot
  // be stolen via XSS from localStorage. On page reload the session is
  // restored from the httpOnly refresh cookie (POST /admin/access-token).
  private memoryToken: string | null = null;
  private memoryRefreshToken: string | null = null;

  constructor() {
    // Use localStorage by default, fallback to sessionStorage if needed
    this.storage = typeof window !== 'undefined' ? window.localStorage : ({} as Storage);
    // Purge tokens persisted by older versions of the app
    this.removeItem(API_CONFIG.STORAGE_KEYS.TOKEN);
    this.removeItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
  }

  // Generic storage methods
  private setItem(key: string, value: any): void {
    try {
      const serializedValue = typeof value === 'string' ? value : JSON.stringify(value);
      this.storage.setItem(key, serializedValue);
    } catch (error) {
      console.error(`Error saving to storage (${key}):`, error);
    }
  }

  private getItem<T>(key: string): T | null {
    try {
      const item = this.storage.getItem(key);
      if (!item) return null;
      
      try {
        return JSON.parse(item) as T;
      } catch {
        // If parsing fails, return as string
        return item as any;
      }
    } catch (error) {
      console.error(`Error reading from storage (${key}):`, error);
      return null;
    }
  }

  private removeItem(key: string): void {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      console.error(`Error removing from storage (${key}):`, error);
    }
  }

  // Token management (in memory only — see note above)
  setToken(token: string): void {
    this.memoryToken = token;
  }

  getToken(): string | null {
    return this.memoryToken;
  }

  removeToken(): void {
    this.memoryToken = null;
  }

  hasToken(): boolean {
    return !!this.memoryToken;
  }

  // Refresh token management (the real refresh token lives in an httpOnly
  // cookie managed by the backend; this is kept for API compatibility)
  setRefreshToken(token: string): void {
    this.memoryRefreshToken = token;
  }

  getRefreshToken(): string | null {
    return this.memoryRefreshToken;
  }

  removeRefreshToken(): void {
    this.memoryRefreshToken = null;
  }

  // User data management
  setUser(user: StoredUser): void {
    console.log('💾 [Storage] Saving user data...');
    console.log('👤 [Storage] User:', {
      id: user.id,
      email: user.email,
      username: user.username,
      roleName: user.roleName
    });
    this.setItem(API_CONFIG.STORAGE_KEYS.USER, user);
    console.log('✅ [Storage] User data saved successfully');
  }

  getUser(): StoredUser | null {
    const user = this.getItem<StoredUser>(API_CONFIG.STORAGE_KEYS.USER);
    console.log('🔍 [Storage] Getting user:', user ? `✓ ${user.email}` : '✗ Not found');
    return user;
  }

  removeUser(): void {
    console.log('🗑️ [Storage] Removing user data...');
    this.removeItem(API_CONFIG.STORAGE_KEYS.USER);
  }

  // Permissions management
  setPermissions(permissions: string[]): void {
    console.log('💾 [Storage] Saving permissions:', permissions.length, 'permissions');
    console.log('🔐 [Storage] Permissions:', permissions.slice(0, 5).join(', '), '...');
    this.setItem(API_CONFIG.STORAGE_KEYS.PERMISSIONS, permissions);
    console.log('✅ [Storage] Permissions saved successfully');
  }

  getPermissions(): string[] | null {
    const permissions = this.getItem<string[]>(API_CONFIG.STORAGE_KEYS.PERMISSIONS);
    console.log('🔍 [Storage] Getting permissions:', permissions ? `✓ ${permissions.length} found` : '✗ Not found');
    return permissions;
  }

  removePermissions(): void {
    this.removeItem(API_CONFIG.STORAGE_KEYS.PERMISSIONS);
  }

  hasPermission(permission: string): boolean {
    const permissions = this.getPermissions();
    return permissions ? permissions.includes(permission) : false;
  }

  // Language preference
  setLanguage(language: 'en' | 'ar'): void {
    this.setItem(API_CONFIG.STORAGE_KEYS.LANGUAGE, language);
  }

  getLanguage(): 'en' | 'ar' {
    return this.getItem<'en' | 'ar'>(API_CONFIG.STORAGE_KEYS.LANGUAGE) || 'en';
  }

  // Theme preference
  setTheme(theme: 'light' | 'dark'): void {
    this.setItem(API_CONFIG.STORAGE_KEYS.THEME, theme);
  }

  getTheme(): 'light' | 'dark' {
    return this.getItem<'light' | 'dark'>(API_CONFIG.STORAGE_KEYS.THEME) || 'light';
  }

  // Clear all auth-related data
  clearAuth(): void {
    this.removeToken();
    this.removeRefreshToken();
    this.removeUser();
    this.removePermissions();
  }

  // Clear all storage
  clearAll(): void {
    this.memoryToken = null;
    this.memoryRefreshToken = null;
    try {
      this.storage.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
    }
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return this.hasToken() && this.getUser() !== null;
  }

  // Get user role
  getUserRole(): string | null {
    const user = this.getUser();
    return user?.roleName || user?.role?.name || null;
  }

  // Check if user has specific role
  hasRole(roleName: string): boolean {
    const userRole = this.getUserRole();
    return userRole === roleName;
  }

  // Check if user has any of the specified roles
  hasAnyRole(roleNames: string[]): boolean {
    const userRole = this.getUserRole();
    return userRole ? roleNames.includes(userRole) : false;
  }

  // Get user full name
  getUserFullName(): string {
    const user = this.getUser();
    if (!user) return 'Guest';
    return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'User';
  }

  // Check if user account is active
  isUserActive(): boolean {
    const user = this.getUser();
    return user?.isActive ?? false;
  }
}

// Export singleton instance
export const storageService = new StorageService();

// Export default for convenience
export default storageService;

